import React, { useEffect, useRef, useState } from 'react';
import ansesConexionService from '../../services/ansesConexionService';
import '../../style/components/ANSESLoginModal.css';

const POLL_VNC_MS = 2000;
const POLL_AUTO_MS = 4000;
// Margen generoso antes de rendirse y ofrecer el botón manual (2captcha puede tardar minutos).
const MAX_ESPERA_AUTOMATICA_MS = 6 * 60 * 1000;

const PASOS_CARGA = [
    { texto: 'Iniciando sesión de navegación…',        icono: 'bi-display' },
    { texto: 'Cargando formulario de ANSES…',          icono: 'bi-globe2' },
    { texto: 'Completando credenciales…',              icono: 'bi-key-fill' },
    { texto: 'Resolviendo el captcha automáticamente…', icono: 'bi-shield-check' },
    { texto: 'Esperando confirmación de ANSES…',       icono: 'bi-hourglass-split' },
];
// Cuánto tiempo mostrar cada paso (ms desde que empieza 'conectando')
const TIEMPOS_PASOS = [0, 4000, 9000, 15000, 30000];

function ANSESLoginModal({ open, cuil, nombre, onClose, onConectado }) {
    const [fase, setFase] = useState('verificando');
    const [clave, setClave] = useState('');
    const [vncUrl, setVncUrl] = useState('');
    const [sessionId, setSessionId] = useState(null);
    const [mensajeError, setMensajeError] = useState('');
    const [indicePaso, setIndicePaso] = useState(0);
    // Si hay clave (propia o guardada), el intento automático sigue solo en segundo plano aunque
    // se cierre el modal; sin clave, el VNC requiere que un humano resuelva el captcha a mano.
    const [claveDisponible, setClaveDisponible] = useState(false);
    const pollRef = useRef(null);
    const timersPasosRef = useRef([]);
    const timerConectadoRef = useRef(null);

    const limpiarTimers = () => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        timersPasosRef.current.forEach(clearTimeout);
        timersPasosRef.current = [];
        if (timerConectadoRef.current) { clearTimeout(timerConectadoRef.current); timerConectadoRef.current = null; }
    };

    // Interpreta la respuesta con sessionId (implica VNC, aunque a veces se resuelva solo por stealth).
    const manejarRespuestaConSesion = (resultado) => {
        const { sessionId: id, vncUrl: url, autoLogin } = resultado;
        setSessionId(id);

        if (autoLogin) {
            setFase('exitoso');
            timerConectadoRef.current = setTimeout(() => onConectado?.(), 1500);
            return;
        }

        setVncUrl(url);
        setFase('esperando');

        pollRef.current = setInterval(async () => {
            try {
                const { estado } = await ansesConexionService.pollLoginSesion(id);
                if (estado === 'EXITOSO') {
                    limpiarTimers();
                    setFase('exitoso');
                    timerConectadoRef.current = setTimeout(() => onConectado?.(), 1200);
                } else if (estado === 'ERROR' || estado === 'EXPIRADO') {
                    limpiarTimers();
                    setFase('error');
                    setMensajeError(
                        estado === 'EXPIRADO'
                            ? 'La sesión expiró por inactividad.'
                            : 'No se pudo completar el login.'
                    );
                }
            } catch (_) {
                // error de red puntual, se reintenta en el próximo tick
            }
        }, POLL_VNC_MS);
    };

    // Plan A: intento automático en background — poll-ea el estado hasta ACTIVE o ERROR.
    const pollearEstadoAutomatico = () => {
        const inicio = Date.now();
        pollRef.current = setInterval(async () => {
            try {
                const data = await ansesConexionService.obtenerEstado(cuil);
                if (data?.estado === 'ACTIVE') {
                    limpiarTimers();
                    setFase('exitoso');
                    timerConectadoRef.current = setTimeout(() => onConectado?.(), 1200);
                } else if (data?.estado === 'ERROR') {
                    limpiarTimers();
                    setFase('error');
                    setMensajeError(
                        data?.mensajeError
                            ? `No se pudo conectar automáticamente: ${data.mensajeError}`
                            : 'No se pudo conectar automáticamente a ANSES.'
                    );
                } else if (Date.now() - inicio > MAX_ESPERA_AUTOMATICA_MS) {
                    limpiarTimers();
                    setFase('error');
                    setMensajeError('La conexión automática está tardando demasiado.');
                }
            } catch (_) {
                // error de red puntual, se reintenta en el próximo tick
            }
        }, POLL_AUTO_MS);
    };

    // usarClaveGuardada=true omite enviar la clave — el backend la lee de la BD cifrada.
    const iniciarConexion = async (usarClaveGuardada = false) => {
        setFase('conectando');
        setMensajeError('');
        setClaveDisponible(usarClaveGuardada || !!clave);
        try {
            const claveSend = usarClaveGuardada ? undefined : (clave || undefined);
            const resultado = await ansesConexionService.iniciarConexion(cuil, claveSend);

            if (resultado.estado === 'INTENTANDO_AUTOMATICO') {
                pollearEstadoAutomatico();
                return;
            }
            manejarRespuestaConSesion(resultado);
        } catch (e) {
            setFase('error');
            setMensajeError('No se pudo iniciar la conexión con ANSES.');
        }
    };

    // Plan B explícito: solo se ofrece tras un fallo del intento automático (fase 'error').
    const conectarManual = async () => {
        setFase('conectando');
        setMensajeError('');
        try {
            const resultado = await ansesConexionService.iniciarConexionManual(cuil);
            manejarRespuestaConSesion(resultado);
        } catch (e) {
            setFase('error');
            setMensajeError('No se pudo iniciar la conexión manual con ANSES.');
        }
    };

    // Al abrir: con clave guardada auto-conecta; sin clave, muestra el formulario.
    useEffect(() => {
        if (!open) {
            limpiarTimers();
            setFase('verificando');
            setClave('');
            setVncUrl('');
            setSessionId(null);
            setMensajeError('');
            setIndicePaso(0);
            setClaveDisponible(false);
            return;
        }

        let cancelado = false;
        ansesConexionService.obtenerEstado(cuil)
            .then((data) => {
                if (cancelado) return;
                const hayClaveGuardada = !!data?.tieneClaveGuardada;
                setClaveDisponible(hayClaveGuardada);
                if (hayClaveGuardada) {
                    iniciarConexion(true);
                } else {
                    setFase('form');
                }
            })
            .catch(() => {
                if (!cancelado) setFase('form');
            });

        return () => {
            cancelado = true;
            limpiarTimers();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, cuil]);

    // Mensajes progresivos durante la fase 'conectando'
    useEffect(() => {
        if (fase !== 'conectando') return;
        setIndicePaso(0);
        const timers = TIEMPOS_PASOS.slice(1).map((delay, i) =>
            setTimeout(() => setIndicePaso(i + 1), delay)
        );
        timersPasosRef.current = timers;
        return () => timers.forEach(clearTimeout);
    }, [fase]);

    if (!open) return null;

    const etiquetaCliente = nombre
        ? <><strong>{nombre}</strong><span className="text-muted" style={{ fontSize: 13 }}> — CUIL {cuil}</span></>
        : <>CUIL <strong>{cuil}</strong></>;

    return (
        <div className="anses-modal-overlay" role="dialog" aria-modal="true" aria-label="Conectar Mi ANSES">
            <div className={`anses-modal-content${fase === 'esperando' ? ' anses-modal-vnc' : ''}`}>
                <div className="anses-modal-header">
                    <h4>Conectar Mi ANSES</h4>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
                        Cerrar
                    </button>
                </div>

                <div className="anses-modal-body">

                    {/* Verificación inicial brevísima */}
                    {fase === 'verificando' && (
                        <div className="anses-loader-block">
                            <div className="spinner-border text-primary" role="status" />
                            <p>Verificando credenciales guardadas…</p>
                        </div>
                    )}

                    {/* Formulario: solo aparece si no hay clave guardada */}
                    {fase === 'form' && (
                        <>
                            <p className="anses-modal-help">
                                {etiquetaCliente}. Si cargás la clave de Mi ANSES, el sistema intenta conectarse
                                solo (sin pedirte que resuelvas nada) usando resolución automática de captcha.
                                Se guarda cifrada para futuras sincronizaciones.
                            </p>
                            <input
                                type="password"
                                className="form-control"
                                placeholder="Clave de Mi ANSES (opcional)"
                                value={clave}
                                onChange={(e) => setClave(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && iniciarConexion(false)}
                                style={{ marginBottom: 14 }}
                            />
                            <button
                                type="button"
                                className="btn btn-primary w-100"
                                onClick={() => iniciarConexion(false)}
                            >
                                <i className="bi bi-plug" style={{ marginRight: 6 }} />
                                {clave ? 'Conectar automáticamente' : 'Conectar manualmente (sin clave)'}
                            </button>
                        </>
                    )}

                    {/* Cargando con pasos progresivos */}
                    {fase === 'conectando' && (
                        <div className="anses-loader-block">
                            <div className="spinner-border text-primary" role="status" />
                            <p style={{ fontWeight: 500, marginBottom: 4 }}>
                                <i className={`bi ${PASOS_CARGA[indicePaso].icono}`} style={{ marginRight: 7 }} />
                                {PASOS_CARGA[indicePaso].texto}
                            </p>
                            <div className="anses-pasos-track">
                                {PASOS_CARGA.map((_, i) => (
                                    <span
                                        key={i}
                                        className={`anses-paso-dot ${i <= indicePaso ? 'activo' : ''}`}
                                    />
                                ))}
                            </div>
                            <small style={{ color: '#64748b', fontSize: 12, marginTop: 6, display: 'block' }}>
                                Puede tardar varios minutos — estamos resolviendo el captcha automáticamente.
                                Podés cerrar esta ventana y seguir trabajando: la conexión sigue intentando
                                sola en segundo plano.
                            </small>
                        </div>
                    )}

                    {/* VNC para captcha manual (Plan B, o cuando no hay clave para intentar) */}
                    {fase === 'esperando' && vncUrl && (
                        <>
                            <p className="anses-modal-help">
                                Resolvé el captcha y completá el login en la ventana de abajo. La sincronización
                                arranca sola apenas detectamos el login exitoso.
                                {claveDisponible
                                    ? ' Mientras tanto seguimos intentando resolverlo solos en paralelo — podés cerrar esta ventana y seguir trabajando, si lo logramos se conecta sin que hagas nada.'
                                    : ' Como no hay una clave guardada, alguien tiene que completar el login acá a mano — podés cerrar y volver más tarde a terminarlo.'}
                            </p>
                            <iframe
                                src={vncUrl}
                                title="Login ANSES"
                                className="anses-vnc-frame"
                                allow="clipboard-read; clipboard-write"
                            />
                        </>
                    )}

                    {fase === 'exitoso' && (
                        <div className="anses-loader-block">
                            <i className="bi bi-check-circle-fill" style={{ fontSize: 32, color: '#166534' }} />
                            <p>Login exitoso. Sincronizando expedientes…</p>
                        </div>
                    )}

                    {fase === 'error' && (
                        <>
                            <div className="alert alert-danger">{mensajeError}</div>
                            <p className="anses-modal-help">
                                No pudimos conectarnos solos. Podés completar el login manualmente por VNC —
                                vas a tener que resolver el captcha vos mismo.
                            </p>
                            <button
                                type="button"
                                className="btn btn-primary w-100"
                                onClick={conectarManual}
                            >
                                <i className="bi bi-plug me-2" />
                                Conectar manualmente (VNC)
                            </button>
                        </>
                    )}

                </div>
            </div>
        </div>
    );
}

export default ANSESLoginModal;
